import { orderBy } from "lodash-es";
import tableFunctionConvertor from "../../charts/TableView/functions/tableFunctionConvertor";
import formatter from "../formatters/formatter";
import tableOpenMathFunctionConvertor from "../../charts/TableView/functions/tableOpenMathFunctionConvertor";
import tableNewFormulaFunctionConvertor from "../../charts/TableView/functions/tableNewFormulaFunctionConverter";
import { isValid, parse, format, addDays } from "date-fns";
import { getMappingType } from "../getVisualizationLabel";
import { dateFromQuarter } from "./dateFunc";
import { daysOfWeek } from "../constants/constants";
import {
  SORT_TYPES,
  FIELD_TYPE_TO_SORT_TYPE,
  MONTH_YEAR_FORMATS,
  TENURE_ORDER,
} from "./dateSortingTypes";

export {
  SORT_TYPES,
  SORT_TYPE_DEFINITIONS,
  FIELD_TYPE_TO_SORT_TYPE,
  MONTH_YEAR_FORMATS,
  TENURE_ORDER,
} from "./dateSortingTypes";

const SUNDAY = new Date(2023, 0, 1);

const DAY_OF_WEEK_INDEX_BY_NAME = daysOfWeek.reduce((acc, _, index) => {
  const date = addDays(SUNDAY, index);
  acc[format(date, "EEEE").toLowerCase()] = index;
  acc[format(date, "EEE").toLowerCase()] = index;
  return acc;
}, {});

const sortHourOfDay = ({ key, sortType }, data, direction) => {
  const formatted = data.map((item) => ({
    ...item,
    formatted: formatter(item[key], sortType),
    sortedKey: parseInt(item[key]),
  }));

  const am = formatted.filter((f) => f.formatted.toLowerCase().includes("am"));
  const pm = formatted.filter((f) => f.formatted.toLowerCase().includes("pm"));

  const result = [
    ...orderBy(am, ["sortedKey"], ["asc"]),
    ...orderBy(pm, ["sortedKey"], ["asc"]),
  ];

  return direction.toLowerCase() === "asc" ? result : result.reverse();
};

const sortDates = (key, data, direction) => {
  function stringToDateObject(date) {
    if (date && !isNaN(Date.parse(date))) {
      return new Date(date);
    }

    return date;
  }

  const clonnedArray = data.map((d) => ({
    ...d,
    sortedKey: stringToDateObject(d[key] ?? "1900-01-01"), // use not default 1970 year
  }));

  const sorted = clonnedArray.sort((a, b) => a.sortedKey - b.sortedKey);
  return direction.toLowerCase() === "asc" ? sorted : sorted.reverse();
};

const sortTenures = (key, data, direction) => {
  const clonnedArray = data.map((d) => ({
    ...d,
    sortedKey: TENURE_ORDER.findIndex((tenure) => tenure === d[key]),
  }));

  return orderBy(clonnedArray, "sortedKey", direction.toLowerCase());
};

export function sortDatesCallback(a, b, direction) {
  const isAsc = direction?.toLowerCase() === "asc";

  const aTime = a instanceof Date ? a.getTime() : a;
  const bTime = b instanceof Date ? b.getTime() : b;

  return isAsc ? aTime - bTime : bTime - aTime;
}

const sortWeekRange = (key, data, direction, separator = " ") => {
  const clonnedArray = data.map((d) => {
    const [startWeek] = String(d[key]).split(separator);

    const date = parse(startWeek.trim(), "MM/dd/yyyy", new Date());

    return {
      ...d,
      sortedKey: isValid(date) ? date : d[key],
    };
  });

  return clonnedArray.sort((a, b) =>
    sortDatesCallback(a.sortedKey, b.sortedKey, direction)
  );
};

export function parseQuarterString(value) {
  if (value == null || value === "") return null;

  const match = String(value)
    .trim()
    .match(/Q\s*(\d)[^\d]*(\d{4})|(\d{4})[^\d]*Q\s*(\d)/i);

  if (!match) return null;

  return {
    quarter: +(match[1] || match[4]),
    year: +(match[2] || match[3]),
  };
}

const sortQuarter = (key, data, direction) => {
  const clonedArray = data.map((d) => {
    const parsed = parseQuarterString(d[key]);

    return {
      ...d,
      sortedKey: parsed ? dateFromQuarter(parsed.quarter, parsed.year) : d[key],
    };
  });

  return clonedArray.sort((a, b) =>
    sortDatesCallback(a.sortedKey, b.sortedKey, direction)
  );
};

export function parseMonthYearString(value) {
  if (value == null || value === "") return null;

  const str = String(value).trim();

  for (const fmt of MONTH_YEAR_FORMATS) {
    const date = parse(str, fmt, new Date());
    if (isValid(date)) return date;
  }

  return null;
}

const sortMonthYear = (key, data, direction) => {
  const clonedArray = data.map((d) => ({
    ...d,
    sortedKey: parseMonthYearString(d[key]) ?? d[key],
  }));

  return clonedArray.sort((a, b) =>
    sortDatesCallback(a.sortedKey, b.sortedKey, direction)
  );
};

export function getDayOfWeekIndex(value) {
  if (value == null || value === "") return -1;

  return DAY_OF_WEEK_INDEX_BY_NAME[String(value).trim().toLowerCase()] ?? -1;
}

const sortDayOfWeek = (key, data, direction) => {
  const clonedArray = data.map((d) => ({
    ...d,
    sortedKey: getDayOfWeekIndex(d[key]),
  }));

  return orderBy(clonedArray, "sortedKey", direction.toLowerCase());
};

export function applySpecialSorting(special = {}, data, direction) {
  const { key, sortType } = special ?? {};

  switch (sortType) {
    case SORT_TYPES.HOUR_OF_DAY:
      return sortHourOfDay(special, data, direction);

    case SORT_TYPES.DATE:
      return sortDates(key, data, direction);

    case SORT_TYPES.TENURE:
      return sortTenures(key, data, direction);

    case SORT_TYPES.WEEK_RANGE:
      return sortWeekRange(key, data, direction, "-");

    case SORT_TYPES.WEEK:
      return sortWeekRange(key, data, direction);

    case SORT_TYPES.QUARTER:
      return sortQuarter(key, data, direction);

    case SORT_TYPES.MONTH_YEAR:
      return sortMonthYear(key, data, direction);

    case SORT_TYPES.DAY_OF_WEEK:
      return sortDayOfWeek(key, data, direction);

    default:
      return data;
  }
}

export function getSpecialSortingType(config, key, type) {
  const existing = config.find((item) => item.key === key);

  if (existing) {
    return existing;
  }

  const sortType = FIELD_TYPE_TO_SORT_TYPE[type];

  return sortType ? { key, sortType } : null;
}

export const sortByKey =
  (
    key,
    direction = "ASC",
    regularFormatting,
    specialSortingTypes = [],
    formatOverrides,
    coloredColumnEdges,
    rowGroupKey,
    meta
  ) =>
  (data) => {
    if (!data.filter((d) => d ?? d === 0).length) {
      return data;
    }

    const type = getMappingType(meta?.fields ?? [], key);
    const special = getSpecialSortingType(specialSortingTypes, key, type);

    if (special) {
      return applySpecialSorting(special, data, direction);
    }

    if (key?.includes("::")) {
      const multiplier = getFormulaSortMultiplier(key);
      const precalculatePercents = data.map((d) => ({
        ...d,
        sortedKey: multiplier * getSortValueFromKey(d, key, regularFormatting),
      }));

      return orderBy(
        precalculatePercents,
        ["sortedKey"],
        [direction.toLowerCase()]
      );
    }

    const prepareForSortData = data.map((item) => {
      // multiplier should be calculated separate for each item
      // because in format overrides we can have different condition values on same key
      const multiplier = getColorMultiplierOnSorting(
        key,
        formatOverrides,
        coloredColumnEdges,
        rowGroupKey,
        item
      );

      const isString = ["string", "text"].includes(type);

      if (typeof item !== "string") {
        return {
          ...item,
          sortedKey: isString
            ? item[key]
            : setMultiplierToNumbersOnly(item[key], multiplier),
        };
      }

      return item;
    });

    return orderBy(
      prepareForSortData,
      ["sortedKey"],
      [direction.toLowerCase()]
    );
  };

export function getFormulaSortMultiplier(key) {
  return key.includes("reverseColor") || key.includes("ReverseColor") ? -1 : 1;
}

export function getSortValueFromKey(row, key, regularFormatting) {
  let value;

  if (key.includes("omf::")) {
    value = tableOpenMathFunctionConvertor(key, row).value;
  } else if (key.includes("snf::")) {
    value = tableNewFormulaFunctionConvertor(key, row).value;
  } else {
    value = tableFunctionConvertor(key, row, null, regularFormatting).value;
  }

  return getValueForSort(value);
}

const getValueForSort = (value) => {
  if (["--", "n/a"].some((x) => x === value) || (!value && value !== 0)) {
    return -Infinity;
  }

  return isNaN(+value) ? value : +value;
};

const getColorMultiplierOnSorting = (
  key,
  formatOverrides,
  coloredColumnEdges,
  rowGroupKey,
  item
) => {
  const formatOverride = (formatOverrides ?? []).find(
    (fOverride) =>
      fOverride.overrideKey === key &&
      item[rowGroupKey] === fOverride.conditionValue
  );

  const coloredColumnEdge = (coloredColumnEdges ?? []).find(
    (ccEdge) => ccEdge.columnName === key
  );

  if (coloredColumnEdge?.reverse || formatOverride?.reverseColor) {
    return -1;
  }

  return 1;
};

const setMultiplierToNumbersOnly = (value, multiplier) => {
  return isNaN(+value)
    ? getValueForSort(value)
    : multiplier * getValueForSort(value);
};
